import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { useTheme } from '../../hooks/useTheme';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { Building2, Truck, Wrench, User, Mail, Smartphone, Lock, Eye, EyeOff } from 'lucide-react';

type Role = 'broker' | 'carrier' | 'service_provider' | null;
type Step = 'role' | 'form' | 'verify';

const ROLES = [
  {
    id: 'broker' as Role,
    label: 'Cliente',
    description: 'Publico cargas y contrato transporte',
    icon: Building2,
    examples: 'Exportadoras, distribuidoras, industrias',
    color: {
      text:    'text-broker dark:text-broker-dark-text',
      badge:   'bg-broker text-white',
    },
  },
  {
    id: 'carrier' as Role,
    label: 'Transportista',
    description: 'Ofrezco mi flota y transporto cargas',
    icon: Truck,
    examples: 'Camioneros independientes, empresas de transporte',
    color: {
      text:    'text-carrier dark:text-carrier-dark-text',
      badge:   'bg-carrier text-white',
    },
  },
  {
    id: 'service_provider' as Role,
    label: 'Proveedor de servicios',
    description: 'Ofrezco servicios auxiliares de transporte',
    icon: Wrench,
    examples: 'Patios, grúas, agentes de aduana, custodias',
    color: {
      text:    'text-provider dark:text-provider-dark-text',
      badge:   'bg-provider text-white',
    },
  },
] as const;

interface FormData {
  name:      string;
  email:     string;
  phone:     string;
  password:  string;
  confirm:   string;
  terms:     boolean;
}

interface FormErrors {
  name?:     string;
  email?:    string;
  phone?:    string;
  password?: string;
  confirm?:  string;
  terms?:    string;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [step, setStep]       = useState<Step>('role');
  const [role, setRole]       = useState<Role>(null);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [apiError, setApiError] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState('');

  const [form, setForm] = useState<FormData>({
    name: '', email: '', phone: '', password: '', confirm: '', terms: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  function setField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(p => ({ ...p, [key]: value }));
    setErrors(p => ({ ...p, [key]: undefined }));
  }

  function validateForm() {
    const e: FormErrors = {};
    if (!form.name.trim())   e.name    = 'Ingresa tu nombre completo';
    if (!form.email)         e.email   = 'Ingresa tu correo electrónico';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Correo electrónico inválido';
    if (!form.phone)         e.phone   = 'Ingresa tu número de WhatsApp';
    else if (!/^\+?[0-9]{8,15}$/.test(form.phone.replace(/\s/g, ''))) e.phone = 'Número inválido. Ej: +56912345678';
    if (!form.password)      e.password = 'Ingresa una contraseña';
    else if (form.password.length < 8) e.password = 'La contraseña debe tener al menos 8 caracteres';
    if (!form.confirm)       e.confirm = 'Confirma tu contraseña';
    else if (form.password !== form.confirm) e.confirm = 'Las contraseñas no coinciden';
    if (!form.terms)         e.terms   = 'Debes aceptar los términos para continuar';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    setApiError('');
    if (!validateForm()) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    setLoading(false);
    setStep('verify');
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setVerifyError('');
    if (verifyCode !== '123456') {
      setVerifyError('Código incorrecto. Usa 123456 para el demo.');
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    localStorage.setItem('auth_user', JSON.stringify({ email: form.email, name: form.name, role }));
    navigate('/dashboard');
  }

  const selectedRole = ROLES.find(r => r.id === role);

  // ── PASO 1: Selección de rol ─────────────────────────────────
  if (step === 'role') {
    return (
      <div className="min-h-screen bg-surface dark:bg-dark-surface flex flex-col">
        <header className="flex items-center justify-between px-4 py-8 max-w-md mx-auto w-full">
          <img
            src={isDark ? '/logo_cargalo-dark.png' : '/logo_cargalo.png'}
            alt="Cárgalo"
            className="h-9 w-auto"
          />
          <ThemeToggle />
        </header>

        <main className="flex-1 flex items-start justify-center px-4 pb-12">
          <div className="w-full max-w-md">

            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-1">Crear cuenta</h1>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted">¿Cuál es tu rol en el transporte de carga?</p>
            </div>

            <div className="space-y-3">
              {ROLES.map(r => {
                const isSelected = role === r.id;
                const IconComponent = r.icon;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    className={[
                      'w-full text-left flex items-start gap-4 p-4 rounded-xl border-2 transition-all duration-150',
                      isSelected
                        ? `border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-slate-800 ring-2 ring-gray-300 dark:ring-gray-600 ring-offset-1`
                        : 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700',
                    ].join(' ')}
                  >
                    {/* Ícono */}
                    <IconComponent className={`w-7 h-7 flex-shrink-0 mt-0.5 ${isSelected ? r.color.text : 'text-gray-600 dark:text-gray-400'}`} strokeWidth={2} />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-base font-bold ${isSelected ? r.color.text : 'text-gray-900 dark:text-dark-text'}`}>
                          {r.label}
                        </span>
                        {isSelected && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.color.badge}`}>
                            Seleccionado
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{r.description}</p>
                      <p className="text-xs text-gray-400 dark:text-dark-text-muted">{r.examples}</p>
                    </div>

                    {/* Check */}
                    <div className={[
                      'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 transition-all',
                      isSelected ? 'border-gray-600 dark:border-gray-400 bg-gray-600 dark:bg-gray-400' : 'border-gray-300 dark:border-gray-600',
                    ].join(' ')}>
                      {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            <Button
              fullWidth
              size="lg"
              className="mt-6"
              disabled={!role}
              onClick={() => setStep('form')}
            >
              Continuar →
            </Button>

            <p className="text-center text-sm text-gray-500 dark:text-dark-text-muted mt-6">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="font-semibold text-broker hover:text-broker-light transition-colors">
                Ingresar
              </Link>
            </p>

          </div>
        </main>
      </div>
    );
  }

  // ── PASO 2: Formulario de datos ──────────────────────────────
  if (step === 'form') {
    return (
      <div className="min-h-screen bg-surface dark:bg-dark-surface flex flex-col">
        <header className="flex items-center justify-between px-4 py-6 max-w-md mx-auto w-full">
          <button
            type="button"
            onClick={() => setStep('role')}
            className="text-sm font-semibold text-gray-600 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text flex items-center gap-1 transition-colors"
          >
            ← Volver
          </button>
          <img
            src={isDark ? '/logo_cargalo-dark.png' : '/logo_cargalo.png'}
            alt="Cárgalo"
            className="h-8 w-auto"
          />
          <ThemeToggle />
        </header>

        <main className="flex-1 flex items-start justify-center px-4 pb-12">
          <div className="w-full max-w-sm">

            {/* Rol seleccionado */}
            {selectedRole && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card mb-6">
                {(() => {
                  const IconComponent = selectedRole.icon;
                  return <IconComponent className={`w-5 h-5 ${selectedRole.color.text}`} strokeWidth={2} />;
                })()}
                <div>
                  <p className={`text-sm font-bold ${selectedRole.color.text}`}>{selectedRole.label}</p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">{selectedRole.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setStep('role')}
                  className="ml-auto text-xs text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-gray-300 underline transition-colors"
                >
                  Cambiar
                </button>
              </div>
            )}

            <div className="mb-6">
              <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text mb-1">Tus datos</h1>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted">Paso 2 de 3 — Información personal</p>
            </div>

            {apiError && <Alert type="error" className="mb-5">{apiError}</Alert>}

            <form onSubmit={handleFormSubmit} noValidate className="space-y-4">

              <Input
                label="Nombre completo"
                type="text"
                placeholder="Ej: Carlos Vera Rojas"
                autoComplete="name"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                error={errors.name}
                leftIcon={<User className="w-4 h-4 text-gray-400" strokeWidth={2} />}
              />

              <Input
                label="Correo electrónico"
                type="email"
                placeholder="tu@correo.cl"
                autoComplete="email"
                value={form.email}
                onChange={e => setField('email', e.target.value)}
                error={errors.email}
                leftIcon={<Mail className="w-4 h-4 text-gray-400" strokeWidth={2} />}
              />

              <Input
                label="WhatsApp"
                type="tel"
                placeholder="+56 9 1234 5678"
                autoComplete="tel"
                value={form.phone}
                onChange={e => setField('phone', e.target.value)}
                error={errors.phone}
                hint="Recibirás notificaciones importantes por WhatsApp"
                leftIcon={<Smartphone className="w-4 h-4 text-gray-400" strokeWidth={2} />}
              />

              <Input
                label="Contraseña"
                type={showPass ? 'text' : 'password'}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                value={form.password}
                onChange={e => setField('password', e.target.value)}
                error={errors.password}
                leftIcon={<Lock className="w-4 h-4 text-gray-400" strokeWidth={2} />}
                rightIcon={
                  <button type="button" onClick={() => setShowPass(v => !v)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" tabIndex={-1}>
                    {showPass ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                  </button>
                }
              />

              <Input
                label="Confirmar contraseña"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Repite tu contraseña"
                autoComplete="new-password"
                value={form.confirm}
                onChange={e => setField('confirm', e.target.value)}
                error={errors.confirm}
                leftIcon={<Lock className="w-4 h-4 text-gray-400" strokeWidth={2} />}
                rightIcon={
                  <button type="button" onClick={() => setShowConfirm(v => !v)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" tabIndex={-1}>
                    {showConfirm ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                  </button>
                }
              />

              {/* Términos */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <div
                    onClick={() => setField('terms', !form.terms)}
                    className={[
                      'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-all cursor-pointer',
                      form.terms ? 'bg-broker border-broker' : 'border-gray-300 bg-white',
                    ].join(' ')}
                  >
                    {form.terms && <span className="text-white text-xs font-bold">✓</span>}
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Acepto los{' '}
                    <Link to="/terms" className="text-broker font-medium hover:underline" target="_blank">
                      Términos de uso
                    </Link>{' '}
                    y la{' '}
                    <Link to="/privacy" className="text-broker font-medium hover:underline" target="_blank">
                      Política de privacidad
                    </Link>
                  </span>
                </label>
                {errors.terms && <p className="text-xs text-danger mt-1.5 ml-8">{errors.terms}</p>}
              </div>

              <Button type="submit" fullWidth size="lg" loading={loading} className="mt-2">
                {loading ? 'Creando cuenta…' : 'Crear cuenta →'}
              </Button>

            </form>

          </div>
        </main>
      </div>
    );
  }

  // ── PASO 3: Verificación de correo ───────────────────────────
  return (
    <div className="min-h-screen bg-surface dark:bg-dark-surface flex flex-col">
      <header className="flex items-center justify-between px-4 py-8 max-w-sm mx-auto w-full">
        <img
          src={isDark ? '/logo_cargalo-dark.png' : '/logo_cargalo.png'}
          alt="Cárgalo"
          className="h-9 w-auto"
        />
        <ThemeToggle />
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-sm">

          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-dark-card flex items-center justify-center">
                <Mail className="w-8 h-8 text-gray-600 dark:text-gray-400" strokeWidth={2} />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-2">Verifica tu correo</h1>
            <p className="text-sm text-gray-500 dark:text-dark-text-muted leading-relaxed">
              Enviamos un código de 6 dígitos a{' '}
              <span className="font-semibold text-gray-700 dark:text-dark-text">{form.email}</span>
            </p>
          </div>

          <Alert type="info" className="mb-6">
            Para este demo, usa el código <strong>123456</strong>
          </Alert>

          {verifyError && <Alert type="error" className="mb-4">{verifyError}</Alert>}

          <form onSubmit={handleVerify} noValidate className="space-y-4">

            {/* Input de código estilizado */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700 dark:text-dark-text">
                Código de verificación
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={e => {
                  setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setVerifyError('');
                }}
                className={[
                  'w-full text-center text-2xl font-bold tracking-[0.5em] py-4 border-2 rounded-xl',
                  'bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text placeholder-gray-300 dark:placeholder-dark-text-muted',
                  'focus:outline-none focus:ring-2 focus:ring-broker focus:border-transparent',
                  'transition-all duration-150',
                  verifyError ? 'border-danger' : 'border-gray-200 dark:border-dark-border',
                ].join(' ')}
              />
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              disabled={verifyCode.length !== 6}
            >
              {loading ? 'Verificando…' : 'Verificar cuenta →'}
            </Button>

          </form>

          <p className="text-center text-sm text-gray-500 dark:text-dark-text-muted mt-6">
            ¿No llegó el código?{' '}
            <button
              type="button"
              className="font-semibold text-broker hover:text-broker-light transition-colors"
            >
              Reenviar código
            </button>
          </p>

          <p className="text-center text-sm text-gray-400 dark:text-dark-text-muted mt-2">
            <button
              type="button"
              onClick={() => setStep('form')}
              className="hover:text-gray-600 dark:hover:text-dark-text transition-colors"
            >
              ← Volver al formulario
            </button>
          </p>

        </div>
      </main>
    </div>
  );
}
